import { useState, useEffect, useRef, useCallback } from "react";
import clsx from "clsx";
import WIconButton from "../wicon-button/WIconButton";
import WLink from "../wlink/WLink";
import WButton from "../wbutton/WButton";
import { useRouter } from "next/router";
import { faBars, faX } from "@fortawesome/pro-regular-svg-icons";
import { createPortal } from "react-dom";

interface Link {
  text: string;
  href?: string;
  icon?: string;
  onClick?: () => void;
}
interface SidebarProps {
  image: {
    url: string;
    altTag: string;
  };
  button?: {
    text: string;
    url: string;
  };
  links: Array<
    Link & {
      subitems?: Array<Link>;
    }
  >;
}

const Sidebar = ({ image, button, links }: SidebarProps) => {
  const router = useRouter();
  const [showSidebar, setShowSidebar] = useState(false); // Controls visibility
  const [isVisible, setIsVisible] = useState(false); // Controls rendering
  const [expandedItems, setExpandedItems] = useState<number[]>([]); // Track expanded items
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const openSidebar = () => {
    setIsVisible(true); // Ensure the sidebar renders
    setTimeout(() => setShowSidebar(true), 10); // Delay to ensure transition starts
  };

  const closeSidebar = () => {
    setShowSidebar(false); // Trigger the sliding out animation
    setTimeout(() => setIsVisible(false), 300); // Remove after animation completes (300ms duration)
  };

  const toggleSubitems = (index: number) => {
    setExpandedItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Check if a link is active based on the current URL
  const isLinkActive = useCallback(
    (href?: string) => {
      if (!href) return false;
      // Check if the current URL matches the link's URL
      return router.asPath === href;
    },
    [router.asPath]
  );

  // Check if a link or any of its subitems is active
  const hasActiveChild = useCallback(
    (link: Link & { subitems?: Array<Link> }): boolean => {
      if (isLinkActive(link.href)) return true;
      if (!link.subitems) return false;
      return link.subitems.some(
        (subitem) =>
          isLinkActive(subitem.href) ||
          hasActiveChild(subitem as Link & { subitems?: Array<Link> })
      );
    },
    [isLinkActive]
  );

  // Update expanded items when URL changes
  useEffect(() => {
    // Find all parent links that have active children and get their indices
    const activeParentIndices = links
      .map((link, index) =>
        link.subitems && hasActiveChild(link) ? index : -1
      )
      .filter((index) => index !== -1);

    // Expand any parents that have active children
    if (activeParentIndices.length > 0) {
      setExpandedItems((prev) => {
        const newIndices = [...prev];
        activeParentIndices.forEach((index) => {
          if (!newIndices.includes(index)) {
            newIndices.push(index);
          }
        });
        return newIndices;
      });
    }
  }, [router.asPath, links, hasActiveChild]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        closeSidebar();
      }
    };

    if (showSidebar) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showSidebar]);

  // Render a link and its subitems if applicable
  const renderLink = (
    link: Link & { subitems?: Array<Link> },
    index: number,
    isSubitem = false
  ) => {
    const hasSubitems = link.subitems && link.subitems.length > 0;
    const isExpanded = expandedItems.includes(index);
    const isActive = isLinkActive(link.href);

    return (
      <div key={index} className={`${isSubitem ? "ml-4" : ""} w-full`}>
        <div className="flex items-center w-full">
          <WLink
            simpleLink
            href={link.href || (hasSubitems ? undefined : "#")}
            text={link.text}
            onClick={() => {
              if (hasSubitems) {
                toggleSubitems(index);
              } else if (link.onClick) {
                link.onClick();
                closeSidebar();
              } else if (link.href) {
                router.push(link.href);
                closeSidebar();
              }
            }}
            className={`mb-4 flex-grow ${hasSubitems ? "cursor-pointer" : ""} ${
              isActive ? "text-white" : ""
            }`}
          />
          {hasSubitems && (
            <span className="text-white text-xl ml-2 mb-4">
              {isExpanded ? "-" : "+"}
            </span>
          )}
        </div>

        {/* Render subitems if expanded */}
        {hasSubitems && isExpanded && (
          <div className="pl-2 border-l border-white/20">
            {link.subitems!.map((subitem, subIndex) =>
              renderLink(subitem, index * 1000 + subIndex, true)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Hamburger Icon */}
      <WIconButton
        type="transparent-white"
        icon={faBars}
        className="text-2xl text-[#fff] lg:hidden"
        onClick={openSidebar}
      />
      {/* Sidebar */}
      {isVisible &&
        createPortal(
          <div
            className={clsx(
              "fixed inset-0 bg-black/30 z-[99998] transition-opacity",
              showSidebar ? "opacity-100 visible" : "opacity-0 invisible"
            )}
            onClick={closeSidebar} // Close sidebar when clicking the backdrop
          >
            <div
              ref={sidebarRef}
              className={clsx(
                "fixed top-0 right-0 z-[99999] flex flex-col items-start w-3/4 max-w-xs h-full px-4 py-6 bg-ekblue transform transition-transform duration-300 overflow-y-auto",
                showSidebar ? "translate-x-0" : "translate-x-full"
              )}
              onClick={(e) => e.stopPropagation()} // Prevent backdrop click
            >
              {/* Header with Logo and Close Button */}
              <div className="flex items-center justify-between w-full mb-6">
                <img
                  src={image.url}
                  alt={image.altTag}
                  className="h-10 cursor-pointer"
                  onClick={() => {
                    router.push("/");
                    closeSidebar();
                  }}
                />
                <WIconButton
                  icon={faX}
                  type="transparent-white"
                  className="text-2xl"
                  onClick={closeSidebar}
                />
              </div>

              {/* Links with subitems support */}
              {links.map((link, index) => renderLink(link, index))}

              {/* Button (Optional) */}
              {button?.url && (
                <WButton
                  type="primary-small"
                  text={button.text}
                  onClick={() => {
                    router.push(button.url);
                    closeSidebar();
                  }}
                  className="mt-4"
                />
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Sidebar;
